import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SpraySession, SprayProgram, sprayService } from '../../services/sprayService';
import SpraySessionList from './SpraySessionList';
import SpraySessionForm from './SpraySessionForm';
import SpraySessionDetail from './SpraySessionDetail';

type View = 'list' | 'form' | 'detail';

const SpraySessionsPage: React.FC = () => {
  const [view, setView] = useState<View>('list');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editSession, setEditSession] = useState<SpraySession | undefined>();
  const [refreshKey, setRefreshKey] = useState(0);

  // extend_spray_schema_20260430 spec §6.2: hierarchical URL contract for
  // program/round filtering. roundId without programId is invalid; we strip
  // it from the URL silently rather than rendering a broken filter chip.
  const [searchParams, setSearchParams] = useSearchParams();
  const programIdFilter = searchParams.get('programId') || undefined;
  const roundIdFilter   = searchParams.get('roundId')   || undefined;
  const dayFilter       = searchParams.get('day')        || undefined;

  useEffect(() => {
    if (roundIdFilter && !programIdFilter) {
      const next = new URLSearchParams(searchParams);
      next.delete('roundId');
      setSearchParams(next, { replace: true });
    }
    // We deliberately omit setSearchParams — it's a stable reference
    // from useSearchParams in react-router v6.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programIdFilter, roundIdFilter]);

  // When a program filter is active, fetch the program (once) so the
  // breadcrumb chip can render its name + round name. We cache the lookup
  // by id to avoid re-fetching on every list re-render.
  const [filterProgram, setFilterProgram] = useState<SprayProgram | null>(null);
  useEffect(() => {
    if (!programIdFilter) {
      setFilterProgram(null);
      return;
    }
    let cancelled = false;
    sprayService.getPrograms({ status: 'all' })
      .then(progs => {
        if (cancelled) return;
        const match = progs.find(p => p._id === programIdFilter) || null;
        setFilterProgram(match);
      })
      .catch(() => { if (!cancelled) setFilterProgram(null); });
    return () => { cancelled = true; };
  }, [programIdFilter]);

  const programNameDisplay = filterProgram?.name || (programIdFilter ? programIdFilter.slice(-6) : '');
  const roundNameDisplay = useMemo(() => {
    if (!roundIdFilter || !filterProgram) return roundIdFilter ? roundIdFilter.slice(-6) : '';
    const idx = (filterProgram.rounds || []).findIndex(r => r._id === roundIdFilter);
    if (idx === -1) return roundIdFilter.slice(-6);
    const round = filterProgram.rounds[idx];
    return round.name || `Round ${idx + 1}`;
  }, [roundIdFilter, filterProgram]);

  const clearProgramFilter = useCallback(() => {
    // Removing the program chip while roundId is set also clears roundId
    // (the URL contract is hierarchical — roundId can't be alone).
    const next = new URLSearchParams(searchParams);
    next.delete('programId');
    next.delete('roundId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const clearRoundFilter = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('roundId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const goList = useCallback(() => {
    setView('list');
    setSelectedId(null);
    setEditSession(undefined);
  }, []);

  const handleSave = useCallback(() => {
    setRefreshKey(k => k + 1);
    goList();
  }, [goList]);

  /**
   * Edit-entry helper. The list endpoint doesn't populate `session.combos`,
   * so passing the list-loaded session directly to SpraySessionForm makes
   * the form fall back to the deprecated parent.machineId for every combo
   * row (legacy fallback in deriveInitialCombos). That lies about per-combo
   * machine assignments. Fetch the full session (which includes the real
   * combos array from GET /sessions/:id) before opening the form so the
   * user always sees the actual server state.
   *
   * Detail → Edit already passes a fully-loaded session because
   * SpraySessionDetail fetches via getSession; that path passes the session
   * through this helper too for consistency, but the fetch is a cache-hit
   * (no user-visible latency).
   */
  const openEditForm = useCallback(async (session: SpraySession) => {
    try {
      const full = await sprayService.getSession(session._id);
      setEditSession({ ...full.session, combos: full.combos });
    } catch {
      // If the fetch fails, fall back to the list-loaded session. The form
      // will still work for non-combo edits; per-combo machine fidelity
      // degrades gracefully.
      setEditSession(session);
    }
    setView('form');
  }, []);

  if (view === 'form') {
    return (
      <SpraySessionForm
        session={editSession}
        onSave={handleSave}
        onCancel={goList}
      />
    );
  }

  if (view === 'detail' && selectedId) {
    return (
      <SpraySessionDetail
        sessionId={selectedId}
        onBack={goList}
        onEdit={openEditForm}
      />
    );
  }

  return (
    <SpraySessionList
      key={`${refreshKey}-${programIdFilter || ''}-${roundIdFilter || ''}-${dayFilter || ''}`}
      onNewSession={() => setView('form')}
      onEditSession={openEditForm}
      onViewSession={id => { setSelectedId(id); setView('detail'); }}
      programIdFilter={programIdFilter}
      roundIdFilter={roundIdFilter}
      dayFilter={dayFilter}
      programNameDisplay={programNameDisplay}
      roundNameDisplay={roundNameDisplay}
      onClearProgramFilter={clearProgramFilter}
      onClearRoundFilter={clearRoundFilter}
    />
  );
};

export default SpraySessionsPage;
